<script>
  import { clickOutside } from '@utils/clickOutside';

  import { getCurrentUserStore } from '@stores/current_user';
  import Log from '@utils/log';
  import Track from '@utils/track';

  import UserAjax from '@ajax/user';
  import GlobalSearchAjax from '@ajax/global_search';
  // import AutocompleteAjax from '@ajax/autocomplete';

  import SearchResults from './SearchResults.svelte';
  import SearchInput from './SearchInput.svelte';

  export let mobile = false;
  export let loadInitialResults = false;

  export let userSearch = true;
  export let keywordSearch = true;
  export let searchTerm = '';
  export let autoFocus = false;
  export let showBackButton = false;

  let currentUser = getCurrentUserStore('default');

  let loading = false;
  let offset = 1;
  let hasMore = false;
  let rightIconPath;

  let users = [];
  let keywords = [];
  let searchResults = [];

  let searchCount = 0;
  let selectedIndex = -1;

  let focused = false;

  $: if (users.length > 0 && keywords.length > 0) {
    searchResults = [
      ...keywords.slice(0, 3),
      ...users.slice(0, 3),
      ...keywords.slice(3, 5),
      ...users.slice(3, users.length),
    ];
  } else if (users.length > 0) {
    searchResults = users;
  } else if (keywords.length > 0) {
    searchResults = keywords;
  } else {
    searchResults = [];
  }

  let infoTriggerTimeout;

  let sendSlackAlertAndLog = function () {
    if (searchTerm && searchTerm != '' && currentUser && currentUser.name) {
      if (currentUser.benable_user === false) {
        Log.info(
          `${keywordSearch ? '[GLOBAL] ' : ''}${searchTerm} | searched by ${currentUser.name} (${
            currentUser.profile_url
          })`,
          false,
          '#search-alerts'
        );
      }

      Track.globalSearchSearched({ currentUser: currentUser, term: searchTerm });
    }
  };

  const handleValueChange = async function (newTerm) {
    offset = 1;
    clearTimeout(infoTriggerTimeout);
    infoTriggerTimeout = setTimeout(sendSlackAlertAndLog, 1000);

    searchTerm = newTerm;

    if (searchTerm === '') {
      loadPreviousKeywords();
    }

    await loadData(true);
  };

  const loadUsers = async function (count, refresh = false) {
    if (refresh) {
      if (count != searchCount) {
        return;
      }

      offset = 1;
    }
    const queryVal = searchTerm;

    let response = await UserAjax.searchUsers(searchTerm, offset);
    if (queryVal === searchTerm) {
      if (refresh === false) {
        users = [...users, ...response.users];
      } else {
        users = response.users;
      }

      hasMore = response.users.length > 0;

      if (hasMore) {
        offset += 1;
      }
    }
  };

  const loadPreviousKeywords = async function () {
    if (!keywordSearch) {
      return;
    }

    let previousKeywords = await GlobalSearchAjax.keywords();
    keywords = previousKeywords;
  };

  const loadKeywords = async function (count) {
    if (count != searchCount) {
      return;
    }

    if (searchTerm === '') {
      keywords = [];
      return;
    }

    keywords = [searchTerm];

    // let resp = await AutocompleteAjax.get(searchTerm);
    // keywords = resp.results;
  };

  const loadData = async function () {
    loading = true;
    let currentCount = searchCount + 0;

    if (keywordSearch && userSearch) {
      await Promise.all([await loadKeywords(currentCount), await loadUsers(currentCount, true)]);
    } else if (keywordSearch) {
      await loadKeywords(currentCount);
    } else if (userSearch) {
      await loadUsers(currentCount, true);
    }

    searchCount += 1;
    loading = false;
  };

  const enterPressed = function (e) {
    if (keywordSearch) {
      let obj = searchResults[selectedIndex] || (e && e.detail && e.detail.value) || searchTerm;

      if (obj.id) {
        goToUser(obj, e);
      } else {
        goToKeyword(obj, e);
      }
    } else {
      let obj = searchResults[selectedIndex];

      if (obj) {
        goToUser(obj, e);
      }
    }
  };

  let goToUser = function (user, event = null) {
    if (currentUser && currentUser.name && !currentUser.benable_user) {
      Log.info(
        `${keywordSearch ? '[GLOBAL] ' : ''}[CLICK] ${user.name} (https://benable.com${
          user.profile_path
        }) | searched by ${currentUser.name}`,
        false,
        '#search-alerts'
      );

      Track.globalSearchSelected({
        currentUser,
        user,
        term: searchTerm,
      });
    }
    let newTab = false;
    if (event && (event.ctrlKey || event.metaKey)) {
      newTab = true;
    }
    window.open(user.profile_path, newTab ? '_blank' : '_self');
  };

  let goToKeyword = function (keyword, event = null) {
    if (currentUser && currentUser.name && !currentUser.benable_user) {
      Log.info(`[GLOBAL] [CLICK] keyword: ${keyword} | searched by ${currentUser.name}`, false, '#search-alerts');
    }
    let newTab = false;
    if (event && (event.ctrlKey || event.metaKey)) {
      newTab = true;
    }
    let url = `/global_search?keyword=${encodeURIComponent(keyword)}`;
    window.open(url, newTab ? '_blank' : '_self');
  };

  if (loadInitialResults) {
    loadData();
  }
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  .search-container.mobile {
    margin-left: 0px;
    margin-right: 0px;
    margin-top: 26px;
  }

  .search-container.desktop {
    max-width: 415px;
    flex: 1;
  }
</style>

<div
  use:clickOutside
  on:click_outside={() => {
    if (!document.body.classList.contains('creator-spotlight-show')) {
      focused = false;
    }
  }}
  class="search-container {mobile ? 'mobile' : 'desktop'}">
  <SearchInput
    keywordSearch={keywordSearch}
    mobile={mobile}
    loading={loading}
    searchTerm={searchTerm}
    hasResults={focused && searchResults.length > 0}
    autoFocus={autoFocus}
    showBackButton={showBackButton}
    handleValueChange={(t) => handleValueChange(t)}
    showBetaLabel={keywordSearch}
    bind:rightIconPath={rightIconPath}
    on:focus={() => {
      if (users.length == 0 && keywords.length == 0) {
        if (searchTerm == '') {
          loadPreviousKeywords();
        }

        loadData();
      }

      selectedIndex = -1;
      focused = true;
    }}
    on:keyup-down={() => {
      if (selectedIndex < searchResults.length - 1) {
        selectedIndex += 1;
      }
    }}
    on:keyup-up={() => {
      if (selectedIndex > -1) {
        selectedIndex -= 1;
      }
    }}
    on:enter={(e) => {
      enterPressed(e);
    }}
    on:blur={() => {
      setTimeout(() => {
        if (!keywordSearch) {
          searchTerm = '';
          rightIconPath = null;
          users = [];
        }

        // focused = false;
      }, 200);
    }} />

  {#if focused && searchResults.length > 0}
    <SearchResults
      userSearch={userSearch}
      keywordSearch={keywordSearch}
      mobile={mobile}
      loading={loading}
      offset={offset}
      searchTerm={searchTerm}
      hasMore={hasMore}
      searchResults={searchResults}
      goToUser={goToUser}
      goToKeyword={goToKeyword}
      bind:selectedIndex={selectedIndex}
      on:load-data={() => loadUsers()} />
  {/if}
</div>
